/**
 * Visual Stage — Stage 4: Visual aid validation.
 *
 * ARCHITECTURE v4.0 — Phase 2, Step 2.5
 *
 * Validates:
 * - Required families have visual aids
 * - stimulusAids/supportAids follow semantic convention
 * - Visual types match policy-allowed types
 * - No answer leakage in visual data
 */

import type { ValidationIssue, PipelineStage } from '../issueContract'
import { blocker, coach, info } from '../issueContract'
import { getVisualPolicy, familyRequiresVisual } from '../policyEngine'
import type { Question } from '../../../types'

const STAGE: PipelineStage = 4

/**
 * Run visual validation on a question.
 */
export function runVisualStage(question: Question): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const qId = question.id ?? 'UNKNOWN'
  const fId = question.familyId ?? 'UNKNOWN'

  if (!question.familyId) return issues

  const visualPolicy = getVisualPolicy(question.familyId)
  const isRequired = familyRequiresVisual(question.familyId)

  // ── Required visual check ────────────────────────────────────────────────

  if (isRequired && visualPolicy) {
    // Stimulus required check
    if (visualPolicy.stimulusRequired) {
      const hasStimulus =
        Array.isArray(question.stimulusAids) && question.stimulusAids.length > 0
      if (!hasStimulus) {
        issues.push(
          blocker(
            'visual',
            STAGE,
            'VISUAL_STIMULUS_REQUIRED',
            `Family "${question.familyId}" requires a stimulus visual aid`,
            {
              field: 'stimulusAids',
              questionId: qId,
              familyId: fId,
              suggestion: visualPolicy.stimulusDefault
                ? `Add a "${visualPolicy.stimulusDefault}" visual to stimulusAids`
                : 'Add an appropriate visual to stimulusAids',
            },
          ),
        )
      }
    }

    // Support required check
    if (visualPolicy.supportRequired) {
      const hasSupport =
        Array.isArray(question.supportAids) && question.supportAids.length > 0
      if (!hasSupport) {
        issues.push(
          coach(
            'visual',
            STAGE,
            'VISUAL_SUPPORT_RECOMMENDED',
            `Family "${question.familyId}" should have a support visual aid`,
            {
              field: 'supportAids',
              questionId: qId,
              familyId: fId,
              severity: 50,
              suggestion: visualPolicy.supportDefault
                ? `Add a "${visualPolicy.supportDefault}" visual to supportAids`
                : 'Add an appropriate visual to supportAids',
            },
          ),
        )
      }
    }
  }

  // ── Semantic convention check ────────────────────────────────────────────
  // [] = reviewed, no visuals needed; omit/undefined = unreviewed

  if (
    question.stimulusAids !== undefined &&
    !Array.isArray(question.stimulusAids)
  ) {
    issues.push(
      blocker(
        'visual',
        STAGE,
        'VISUAL_STIMULUS_INVALID_TYPE',
        'stimulusAids must be an array (use [] for reviewed-no-visuals)',
        {
          field: 'stimulusAids',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  if (
    question.supportAids !== undefined &&
    !Array.isArray(question.supportAids)
  ) {
    issues.push(
      blocker(
        'visual',
        STAGE,
        'VISUAL_SUPPORT_INVALID_TYPE',
        'supportAids must be an array (use [] for reviewed-no-visuals)',
        {
          field: 'supportAids',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  // ── Answer leakage check ─────────────────────────────────────────────────

  if (
    Array.isArray(question.stimulusAids) &&
    question.stimulusAids.length > 0
  ) {
    const correctAnswer =
      question.questionType === 'multiple_choice' && question.options
        ? question.options[question.correctAnswer ?? -1]
        : undefined

    if (
      correctAnswer &&
      typeof correctAnswer === 'string' &&
      correctAnswer.trim()
    ) {
      const answerLower = correctAnswer.trim().toLowerCase()
      for (let i = 0; i < question.stimulusAids.length; i++) {
        const aid = question.stimulusAids[i]
        if (aid && typeof aid === 'object' && 'data' in aid) {
          const dataStr = JSON.stringify(aid.data).toLowerCase()
          if (
            dataStr.includes(`"label":"${answerLower}"`) ||
            dataStr.includes(`"value":"${answerLower}"`)
          ) {
            issues.push(
              coach(
                'visual',
                STAGE,
                'VISUAL_ANSWER_LEAKAGE',
                `Stimulus visual at index ${i} may leak the correct answer "${correctAnswer}"`,
                {
                  field: `stimulusAids[${i}]`,
                  questionId: qId,
                  familyId: fId,
                  severity: 60,
                  suggestion:
                    'Remove or modify this visual to not reveal the answer, or move it to supportAids',
                },
              ),
            )
          }
        }
      }
    }
  }

  return issues
}
